import moment from 'moment';
import * as fetcher from '@hydra/utils/fetcher';
import settings from '@hydra/utils/settings';

const ALLOWED_PLANS_TO_SHOW_API_USAGE = ['v2pro', 'v2proy', 'v2prm', 'v2prmy', 'lite', 'litey', 'wf-bronze', 'wf-silver', 'wf-gold'];
const COUPON_CODE_WITHOUT_FREE_TRIAL = ['winbackpro29', 'winbackprm59', 'winbackproy299', 'winbackprmy599'];

export async function getUserInfo () {
  return fetcher.get('/api/user').json();
}

export const isUserCreatedAfter = (user, date) => {
  const userCreatedDate = user && user.createDate && new Date(user.createDate);
  if (!userCreatedDate) {
    return false;
  }
  const afterDate = new Date(date);
  return afterDate < userCreatedDate;
};

export const getShowExpertNotification = (expertStatus) => {
  const EXPERTSTATUS_INTERESTED = 'INTERESTED';

  if (!expertStatus) {
    return false;
  }
  return expertStatus === EXPERTSTATUS_INTERESTED;
};

export const getIsPdfAllowed = (userInfo) => {
  const allowedReports = userInfo && userInfo.subscription && userInfo.subscription.plan && userInfo.subscription.plan.allowedReports;
  // this logic stems from the past
  // -1 used to mean "infinite amount of reports"
  // we don't support pdf counting anymore so the only valid values are now
  //     0: not allowed
  //    -1: allowed
  return allowedReports === -1;
};

export const checkUserHasActiveSubscription = (userInfo) => {
  if (!userInfo) {
    throw new Error('userInfo required');
  }
  return !!(userInfo && userInfo.subscription && userInfo.subscription.plan);
};

export function isPlanLite (plan) {
  return plan &&
    plan.planCode &&
    plan.planCode.includes('lite');
}

export function isCouponCodeWithoutFreeTrial (plan) {
  return plan &&
    plan.couponCode &&
    COUPON_CODE_WITHOUT_FREE_TRIAL.includes(plan.couponCode);
}

export function isLite (user) {
  return user &&
    user.subscription &&
    user.subscription.planCode &&
    user.subscription.planCode.includes('lite');
}

export function isPro (user) {
  return user &&
    user.subscription &&
    user.subscription.planCode &&
    user.subscription.planCode.includes('pro');
}

export function shouldShowApiUsage (user) {
  return user &&
    user.subscription &&
    user.subscription.planCode &&
    ALLOWED_PLANS_TO_SHOW_API_USAGE.includes(user.subscription.planCode);
}

export function isPremium (user) {
  return user &&
    user.subscription &&
    user.subscription.planCode &&
    user.subscription.planCode.includes('prm');
}

export function isLeadgenToolAllowed (user) {
  const allowedLeadgenToolUserIds = settings.get('allowedLeadgenToolUserIds');
  const leadgenToolAllowed = user &&
    user.id &&
    allowedLeadgenToolUserIds &&
    allowedLeadgenToolUserIds.split(',').includes(user.id.toString());
  return isPremium(user) || leadgenToolAllowed;
}

export function getIsDudaUser (user) {
  return user &&
    user.subscription &&
    user.subscription.planCode &&
    user.subscription.planCode === 'duda';
}

export function isWebflowUserAllowedCWV (user) {
  if (user && user.subscription) {
    return !(user.subscription.planCode === 'wf-bronze' || user.subscription.planCode === 'wf-silver');
  }
  return false;
}

export function getIsTrial (user) {
  if (!user || !user.subscription) {
    throw new Error('user required');
  }
  const trialStartDate = new Date(user.subscription.trialStartDate);
  const trialEndDate = new Date(user.subscription.trialEndDate);
  const today = new Date();
  return today.getTime() >= trialStartDate && today.getTime() <= trialEndDate;
}

export function getIsInTrialDuda (trialEndDate) {
  if (!trialEndDate) {
    return false;
  }
  const endDate = new Date(trialEndDate);
  const today = new Date();
  return today.getTime() <= endDate;
}

export function calculateTrialDaysLeft (trialEndDate) {
  const today = new Date();
  return moment(trialEndDate).diff(moment(today), 'days') + 1;
}

export function getTempUserPassword () {
  return Math.random().toString(36);
}

export function hasSubUserAccess (user) {
  if (!user) {
    throw new Error('user required');
  }
  return user.masterUserId === 0 && user.subscription?.plan?.allowedSubusers > 0;
}

export function isMasterUser (user) {
  if (!user) {
    throw new Error('user required');
  }
  return user.masterUserId === 0;
}
